import React from 'react';
import {styled} from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import {Box, CircularProgress, Grid} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useNavigate} from 'react-router-dom';

import {getWidgetsData} from '@shared/services/service-handlers/PrivateService';

import LogoComponent from '@modules/public/main/logo/LogoComponent';
import {
  closedMixin,
  DrawerHeader,
  openedMixin
} from '../../../common/top-app-bar/TopAppBarStyles';
import SmallLogoComponent from '@modules/public/main/small-logo/SmallLogoComponent';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
// import IconComponent from '@components/icon/IconComponent';
import {WidgetButton} from './WidgetsMenuListStyle';
import {Close} from '@mui/icons-material';
import {
  BASE_APPLICATION,
  BASE_ROUTE,
  BASE_ROUTE_PAD
} from 'src/routes/BaseRoute';
import DraggableWidget from '../widgets-view-page/DraggableWidget';
import DropZone from '../widgets-view-page/DropZone';

interface WidgetsMenuListComponentProps {
  drawerOpen: boolean;
  padId?: any;
  applicationId: any;
}

const WidgetsMenuList = (
  props: WidgetsMenuListComponentProps
): React.ReactElement => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const drawerWidth = 240;
  const {drawerOpen = true, applicationId, padId} = props;

  const [state, setState] = React.useState({
    activePad: undefined,
    widgetsList: [],
    loading: false
  });

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: prop => prop !== 'open'
  })(({theme, open}) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme)
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme)
    })
  }));

  const init = async () => {
    try {
      setState((prevState: any) => ({
        ...prevState,
        widgetsList: [],
        loading: true
      }));

      const res = await getWidgetsData();

      setState((prevState: any) => ({
        ...prevState,
        widgetsList: res,
        loading: false
      }));
    } catch (error) {
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeWidget = (): void => {
    navigate(
      `/${BASE_ROUTE}/${BASE_APPLICATION}/${applicationId}/${BASE_ROUTE_PAD}/${padId}`
    );
  };

  const renderWidgets = (): React.ReactElement => {
    return (
      <Grid container spacing={1} style={{padding: '1rem'}}>
        <DropZone>
          {state?.widgetsList?.map((item: any, index: any) => {
            return (
              <DraggableWidget
                widget={item}
                key={item?.id}
                drawerOpen={drawerOpen}
              />
            );
          })}
        </DropZone>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        boxShadow: '0px 1px 6px #0000001F'
      }}>
      <Drawer
        variant="permanent"
        open={drawerOpen}
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <DrawerHeader
          style={{
            padding: '0',
            margin: drawerOpen ? '1px auto 3px 1.5em' : '0 auto'
          }}>
          {drawerOpen ? <LogoComponent /> : <SmallLogoComponent />}
        </DrawerHeader>

        <WidgetButton
          variant="text"
          startIcon={<Close />}
          onClick={() => closeWidget()}>
          {drawerOpen ? 'Widget' : ''}
        </WidgetButton>

        {state?.loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60%'
            }}>
            <CircularProgress />
          </div>
        ) : (
          renderWidgets()
        )}
      </Drawer>
    </Box>
  );
};

export default WidgetsMenuList;
